import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzIcon, BringzzText, BringzzDropdown } from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { alphabets } from './constants';

const Substances = () => {
    const { t } = useTranslation();
    const { backPress, navigate } = useNavigation();
    const { sendRequest } = useRequest();

    const [substances, setSubstances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [sortBy, setSortBy] = useState('name_asc');
    const [alphabetFilter, setAlphabetFilter] = useState('#');

    const observer = useRef();
    const isLoadingMore = useRef(false);

    const sortOptions = [
        { value: 'name_asc', label: t('component.search.sort.name_asc') },
        { value: 'name_desc', label: t('component.search.sort.name_desc') },
        { value: 'latin_asc', label: 'Latin A-Z' },
        { value: 'latin_desc', label: 'Latin Z-A' },
        { value: 'stoff_code', label: 'Stoff Code' },
    ];

    const lastSubstanceElementRef = useCallback(node => {
        if (loading || isLoadingMore.current) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !isLoadingMore.current) {
                isLoadingMore.current = true;
                setPage(prevPage => prevPage + 1);
            }
        }, {
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const fetchSubstances = async (pageNum = 1, reset = false) => {
        try {
            setLoading(true);
            isLoadingMore.current = true;

            const response = await sendRequest({
                method: 'POST',
                url: '/module/productsubstance/searchSubstances',
                data: {
                    query: alphabetFilter === '#' ? null : alphabetFilter,
                    page: pageNum,
                    limit: 20,
                    sortBy: sortBy,
                    sortOrder: 'ASC',
                    allSubstances: alphabetFilter === '#'
                }
            });

            if (response?.status === 'success' && response?.data) {
                let filteredSubstances = response.data;

                if (reset || pageNum === 1) {
                    setSubstances(filteredSubstances);
                } else {
                    setSubstances(prev => [...prev, ...filteredSubstances]);
                }

                setHasMore(filteredSubstances.length === 20);
            }
        } catch (error) {
            console.error('Error fetching substances:', error);
            toast.error(t('component.search.general.error'));
        } finally {
            setLoading(false);
            isLoadingMore.current = false;
        }
    };

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        fetchSubstances(1, true);
    }, [sortBy, alphabetFilter]);

    useEffect(() => {
        if (page > 1) {
            fetchSubstances(page);
        }
    }, [page]);

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
    };

    return (
        <BringzzPageContainer
            className="bg-sand pb-32"
            authenticated={false}
            footerHover={true}
            header={{
                left: <BringzzIcon
                    size="22"
                    icon="IconChevronLeft"
                    folder="LineIcons"
                    className="cursor-pointer"
                    onClick={backPress}
                />,
                title: t('component.search.options.substances') || 'Substances'
            }}
        >
            <div className='pt-2'>
                <div className='rounded-xl bg-white mx-2 py-2'>
                    {/* Alphabet Filter */}
                    <div className='flex items-center overflow-x-auto sticky top-0 bg-white z-10 py-2 border-b' style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                        <BringzzText tag='h3' className='whitespace-nowrap mr-1'>A-Z</BringzzText>
                        <div className='flex space-x-3 sm:space-x-2 min-w-0'>
                            {alphabets.map((char, index) => {
                                const isSelected = char === alphabetFilter;
                                return (
                                    <div
                                        key={index}
                                        className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                            isSelected && "!bg-magic-lilac"
                                        )}
                                        onClick={() => setAlphabetFilter(char)}
                                    >
                                        <BringzzText tag='h5'>{char}</BringzzText>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="p-4 border-b">
                        <BringzzDropdown
                            options={sortOptions}
                            value={sortBy}
                            onChange={handleSortChange}
                            placeholder={t('component.search.general.sort_by')}
                        />
                    </div>

                    {/* Substances List */}
                    {substances.length > 0 ? (
                        substances.map((substance, index) => (
                            <div
                                key={substance.id || index}
                                className={classNames("p-4 border-b flex items-center justify-between", substance.productCount > 0 && "cursor-pointer")}
                                ref={index === substances.length - 1 ? lastSubstanceElementRef : null}
                                onClick={() => substance.productCount > 0 && navigate(`/search/substances/${substance.id}?name=${encodeURIComponent(substance.name || substance.innName || substance.stoffCode)}`)}
                            >
                                <div className="flex items-center gap-2 flex-grow min-w-0">
                                    <BringzzText tag='h4' className="truncate">
                                        {substance.name || substance.innName || `Code: ${substance.stoffCode}`}
                                        {substance.nameLatin ? ` \u2022 ${substance.nameLatin}` : ''}
                                    </BringzzText>
                                    <BringzzText tag='span' className="text-xs text-white font-semibold whitespace-nowrap bg-midnight-blue rounded-full px-2 py-0.5">
                                        {substance.productCount ?? 0}
                                    </BringzzText>
                                </div>
                                {substance.productCount > 0 && (
                                    <BringzzIcon
                                        size="16"
                                        icon="IconChevronRight"
                                        folder="LineIcons"
                                        className="ml-2 text-gray-400 flex-shrink-0"
                                    />
                                )}
                            </div>
                        ))
                    ) : loading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : (
                        <div className="p-4 text-center">
                            <BringzzText tag='p'>{t('component.search.empty.no_substances') || 'No substances found'}</BringzzText>
                        </div>
                    )}

                    {loading && substances.length > 0 && (
                        <div className="flex items-center justify-center p-4">
                            <BringzzIcon
                                size="24"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    )}
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default Substances;
